import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

const SESSION_KEY = 'clarity_session_token';
const API_URL = import.meta.env.VITE_WORKER_API_URL || 'https://clarity-platform-api.ryan-dc4.workers.dev';

export type TenantSummary = {
  id: string;
  slug: string;
  name: string;
  tier: 'pilot' | 'build' | 'studio';
  status: string;
  role: string;
};

export type Me = {
  user: { id: string; email: string };
  tenants: TenantSummary[];
};

export type AuthCtx = {
  loading: boolean;
  me: Me | null;
  token: string | null;
  signOut: () => void;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function readSessionToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(SESSION_KEY);
}

export function writeSessionToken(token: string): void {
  window.localStorage.setItem(SESSION_KEY, token);
}

export function clearSessionToken(): void {
  window.localStorage.removeItem(SESSION_KEY);
}

export async function fetchMe(token: string): Promise<Me> {
  const res = await fetch(`${API_URL}/me`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`me failed: ${res.status}`);
  return res.json() as Promise<Me>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(readSessionToken());
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const t = readSessionToken();
    setToken(t);
    if (!t) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const m = await fetchMe(t);
      setMe(m);
    } catch {
      clearSessionToken();
      setMe(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function signOut() {
    clearSessionToken();
    setToken(null);
    setMe(null);
  }

  return <Ctx.Provider value={{ loading, me, token, signOut, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth requires <AuthProvider>');
  return c;
}
